import React, { useContext, useEffect, useState } from 'react';
import axios from "axios";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import coupleImage from '../../../assets/images/couples/Vector.png';
import { SignupContext } from '../../../context/SignupContext';
import { LoginContext } from '../../../context/LoginContext';

const DesiredPartner = () => {
  // Form data state

  const navigate = useNavigate();

  const { userName, userId: signupUserId, isLoggedIn: signupLoggedIn } = useContext(SignupContext);
  const { userId: loginUserId, isLoggedIn, userData, setUserdata } = useContext(LoginContext);
  const userId = loginUserId || signupUserId;

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectMenuProps = {
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    menuPosition: 'fixed',
    styles: {
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
      control: (base) => ({
        ...base,
        borderRadius: 8,
        minHeight: 42,
        borderColor: '#ccc',
        boxShadow: 'none',
      }),
    },
  };

  const [formData, setFormData] = useState({
    min_age: { label: 21, value: 21 },
    max_age: { label: 40, value: 40 },
    min_height: { label: `4.0 Ft`, value: `4.0` },
    max_height: { label: `6.0 Ft`, value: `6.0` },
    maritalStatus: "",
    country: { label: "India", value: '101' },
    state: "",
    caste: "",
    language: "",
    highestEducation: "",
    occupation: "",
    income: ""
  });

  // Options data state
  const [options, setOptions] = useState({
    caste: [],
    gothra: [],
    cities: [],
    countries: [],
    income: [],
    message: [],
    states: [],
    language: [],
    occupation: [],
    education: [],
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all dropdown options
  useEffect(() => {

    if (!isLoggedIn && !signupLoggedIn) {
      navigate('/login');
      return;
    }

    if (!userId) {
      navigate('/login');
      return;
    }

    const status = Number(userData?.status);
    if (status === 3) {
      navigate('/account-pending-approval');
      return;
    }
    if (status === 9 || status === 0) {
      navigate('/create-profile');
      return;
    }
    if (status === 1) {
      navigate('/recently-joined-matches/');
      return;
    }
    if (status !== 8) {
      navigate('/create-profile');
      return;
    }

    const fetchOptions = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}admin-mat/api/caste.php`
        );

        const resp = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}admin-mat/api/education.php`
        );



        if (!response.data.success) {
          throw new Error(response.data.error || "Failed to fetch options");
        }

        // Transform all options to ensure they're arrays
        const transformedOptions = {
          caste: Array.isArray(response.data.data.caste) ? response.data.data.caste : [],
          gothra: Array.isArray(response.data.data.gothra) ? response.data.data.gothra : [],
          cities: Array.isArray(response.data.data.cities) ? response.data.data.cities : [],
          countries: Array.isArray(response.data.data.countries) ? response.data.data.countries : [],
          income: Array.isArray(response.data.data.income) ? response.data.data.income : [],
          message: Array.isArray(response.data.data.message) ? response.data.data.message : [],
          states: Array.isArray(response.data.data.states) ? response.data.data.states : [],
          language: Array.isArray(response.data.data.language) ? response.data.data.language : [],
          occupation: Array.isArray(resp.data.data.occupation) ? resp.data.data.occupation : [],
          education: Array.isArray(resp.data.data.education) ? resp.data.data.education : []
        };

        setOptions(transformedOptions);

        // Preload India states so Country default (India) has State options ready.
        try {
          const stateResp = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}admin-mat/api/state.php?country=101`
          );
          const states = Array.isArray(stateResp.data?.data?.states)
            ? stateResp.data.data.states
            : [];
          setOptions((prev) => ({ ...prev, states }));
        } catch (stateErr) {
          console.error('Error fetching India states:', stateErr);
        }
      } catch (err) {
        console.error("Error fetching options:", err);
        setError(err.message);
        // Initialize with empty arrays if request fails
        setOptions({
          caste: [],
          gothra: [],
          cities: [],
          countries: [],
          income: [],
          message: [],
          states: [],
          language: [],
          occupation: [],
          education: [],
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchOptions();
  }, []);


  // Handle select changes
  const handleSelectChange = (name, selectedOption) => {
    setFormData(prev => ({
      ...prev,
      [name]: selectedOption
    }));
  };

  const isFilled = (value) => {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') {
      return value.value !== undefined && value.value !== null && value.value !== '';
    }
    return true;
  };

  const joinMulti = (value, key = 'value') => {
    if (!value) return '';
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (item == null) return '';
          if (typeof item === 'object') {
            return String(item[key] ?? item.value ?? item.label ?? '');
          }
          return String(item);
        })
        .filter(Boolean)
        .join(',');
    }
    if (typeof value === 'object') {
      return String(value[key] ?? value.value ?? value.label ?? '');
    }
    return String(value);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!isFilled(formData.min_age)) newErrors.min_age = "Min Age is Required";
    if (!isFilled(formData.max_age)) newErrors.max_age = "Max Age is Required";
    if (!isFilled(formData.min_height)) newErrors.min_height = "Min Height is Required";
    if (!isFilled(formData.max_height)) newErrors.max_height = "Max Height is Required";
    if (!isFilled(formData.maritalStatus)) newErrors.maritalStatus = "Marital Status is Required";
    if (!isFilled(formData.country)) newErrors.country = "Country is Required";
    if (!isFilled(formData.state)) newErrors.state = "State is Required";
    if (!isFilled(formData.caste)) newErrors.caste = "Caste is Required";
    if (!isFilled(formData.language)) newErrors.language = "Language is Required";
    if (!isFilled(formData.highestEducation)) newErrors.highestEducation = "Highest Education is Required";
    if (!isFilled(formData.occupation)) newErrors.occupation = "Occupation is Required";
    if (!isFilled(formData.income)) newErrors.income = "Income is Required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setErrors((prev) => ({
        ...prev,
        api: 'Please fill all required fields marked with * (including Country, State, Caste, Language, Education, Occupation, Income).',
      }));
      return;
    }

    if (!userId) {
      setErrors({ api: 'Session expired. Please login again.' });
      navigate('/login?force=1');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrors({});
      const payload = new FormData();

      payload.append("user_id", userId);
      payload.append("age", `${formData.min_age.value},${formData.max_age.value}`);
      payload.append("height", `${formData.min_height.value},${formData.max_height.value}`);
      payload.append("ms", joinMulti(formData.maritalStatus, 'value'));
      payload.append("country", formData.country?.label || formData.country?.value || 'India');
      payload.append("state", joinMulti(formData.state, 'label'));
      payload.append("caste", joinMulti(formData.caste, 'value'));
      payload.append("motherTongue", joinMulti(formData.language, 'value'));
      payload.append("h_education", formData.highestEducation?.value || formData.highestEducation?.label || '');
      payload.append("occupation", joinMulti(formData.occupation, 'value'));
      payload.append("income", formData.income?.value ?? formData.income?.label ?? '');

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}Register_controller/insertDesiredPartner`,
        payload
      );

      const data = response?.data || {};
      if (data.status === true || data.status === 'true' || data.status === 1) {
        setUserdata({ ...(userData || {}), status: 3, user_id: userId });
        navigate('/account-pending-approval', {
          state: {
            message:
              data.message ||
              'Your account profile is under approval. Please wait for 24 hours and we will get back to you.',
          },
        });
        return;
      }

      const apiMsg = data.message || 'Failed to save desired partner.';
      const st = data.current_status;
      setErrors({
        api: st !== undefined && st !== null
          ? `${apiMsg} (account status: ${st})`
          : apiMsg,
      });
      if (st === 0 || st === 9 || st === '0' || st === '9') {
        setTimeout(() => navigate('/create-profile'), 1200);
      }
    } catch (error) {
      console.log('Error While Posting Data:', error);
      const apiMsg =
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong. Please try again.';
      setErrors({ api: apiMsg });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle country changes
  const handlecountryChange = async (name, selectedOption) => {
    setIsLoading(true);
    const resp = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}admin-mat/api/state.php?country=${selectedOption.value}`
    );
    setOptions(prev => ({
      ...prev,
      states: Array.isArray(resp.data.data.states) ? resp.data.data.states : [],
    }));
    setFormData(prev => ({
      ...prev,
      [name]: selectedOption,
      state: ""
    }));
    setIsLoading(false);
  };




  if (isLoading) {
    return <div className='custom-loader-g'>
      <div class="spinner-border text-danger" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (



    <>

      <div className='d-none d-md-block py-md-5'></div>


      <div className='create-profile-wrapper'>

        <div className='container'>
          <div className="create-profile-container">
            {/* Left Side Form */}
            <div className="create-profile-form-side">
              <h1 className="create-profile-heading text-center mb-0">Desired Partner Profile</h1>
              {/* <p className="create-profile-subheading text-center">Start your journey here</p> */}

              <form className="create-profile-form" onSubmit={handleSubmit}>
                {/* Section 1: Basic Details */}
                <h3 className="form-section-title text-center mb-0">Basic Details</h3>
                {errors.api && (
                  <div className="alert alert-danger text-center">
                    <p className="mb-2">{errors.api}</p>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => navigate('/create-profile')}
                    >
                      Go to Create Profile
                    </button>
                  </div>
                )}

                <div className="d-flex justify-content-start mb-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary desired-back-btn"
                    onClick={() => navigate('/create-profile')}
                  >
                    ← Back to Create Profile
                  </button>
                </div>


                <div className="form-row" style={{flexDirection:"row"}}>
                  <div className="form-group">
                    <label className="form-label"><span>Min Age&nbsp;<span class="d-inline coloashe">*</span></span></label>


                    <Select
                      options={Array.from({ length: 40 }, (_, i) => (21 + (i))).map(val => ({
                        label: val,
                        value: val
                      }))}
                      value={formData.min_age}
                      onChange={(selected) => handleSelectChange("min_age", selected)}
                      {...selectMenuProps} />

                    {errors.min_age && <span className="field-error" style={{ color: 'red', fontSize: '12px' }}>{errors.min_age}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label"><span>Max Age&nbsp;<span class="d-inline coloashe">*</span></span></label>

                    <Select
                      options={Array.from({ length: 40 }, (_, i) => (21 + (i))).map(val => ({
                        label: val,
                        value: val
                      }))}
                      value={formData.max_age}
                      onChange={(selected) => handleSelectChange("max_age", selected)}
                      defaultValue={{ label: 40, value: 40 }}
                      {...selectMenuProps} />

                    {errors.max_age && <span className="field-error" style={{ color: 'red', fontSize: '12px' }}>{errors.max_age}</span>}

                  </div>
                </div>



                <div className="form-row"  style={{flexDirection:"row"}}>

                  <div className="form-group">
                    <label className="form-label"><span>Min Height&nbsp;<span class="d-inline coloashe">*</span></span></label>


                    <Select
                      options={Array.from({ length: 4 }, (_, f) => 4 + f) // 4 to 7
                        .flatMap(feet =>
                          Array.from({ length: 12 }, (_, inches) => {
                            if (feet === 7 && inches > 0) return null;
                            return { label: `${feet}.${inches} Ft`, value: `${feet}.${inches}` }
                          }).filter(Boolean)
                        )}
                      value={formData.min_height}
                      onChange={(selected) => handleSelectChange("min_height", selected)}
                      placeholder="Select Min Height"
                      {...selectMenuProps} />

                    {errors.min_height && <span className="field-error" style={{ color: 'red', fontSize: '12px' }}>{errors.min_height}</span>}

                  </div>

                  <div className="form-group">
                    <label className="form-label"><span>Max Height&nbsp;<span class="d-inline coloashe">*</span></span></label>


                    <Select
                      options={Array.from({ length: 4 }, (_, f) => 4 + f) // 4 to 7
                        .flatMap(feet =>
                          Array.from({ length: 12 }, (_, inches) => {
                            if (feet === 7 && inches > 0) return null;
                            return { label: `${feet}.${inches} Ft`, value: `${feet}.${inches}` }
                          }).filter(Boolean)
                        )}
                      value={formData.max_height}
                      onChange={(selected) => handleSelectChange("max_height", selected)}
                      placeholder="Select Max Height"
                      {...selectMenuProps} />

                    {errors.max_height && <span className="field-error" style={{ color: 'red', fontSize: '12px' }}>{errors.max_height}</span>}

                  </div>

                </div>


                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label"><span>Material Status&nbsp;<span class="d-inline coloashe">*</span></span></label>

                    <Select
                      options={Array("Single", "Divorced", "Widowed").map(val => ({
                        label: val,
                        value: val
                      }))}
                      value={formData.maritalStatus}
                      onChange={(selected) => handleSelectChange("maritalStatus", selected)}
                      placeholder="Select Marital Status"
                      isMulti
                      {...selectMenuProps} />

                    {errors.maritalStatus && <span className="field-error" style={{ color: 'red', fontSize: '12px' }}>{errors.maritalStatus}</span>}
                  </div>

                </div>


                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label"><span>Country:&nbsp;<span class="d-inline coloashe">*</span></span></label>

                    <Select
                      options={options?.countries?.map(val => ({
                        label: Object.values(val).toString(),
                        value: Object.keys(val).toString()
                      }))}
                      value={formData.country}
                      onChange={(selected) => handlecountryChange("country", selected)}
                      placeholder="Select Country"
                      {...selectMenuProps} />

                    {errors.country && <span className="field-error" style={{ color: 'red', fontSize: '12px' }}>{errors.country}</span>}

                  </div>
                  <div className="form-group">
                    <label className="form-label"><span>State:&nbsp;<span class="d-inline coloashe">*</span></span></label>

                    <Select
                      options={options?.states?.map(val => ({
                        label: Object.values(val).toString(),
                        value: Object.keys(val).toString()
                      }))}
                      value={formData.state}
                      onChange={(selected) => handleSelectChange("state", selected)}
                      placeholder="Select State"
                      isMulti
                      {...selectMenuProps} />
                    {errors.state && <span className="field-error" style={{ color: 'red', fontSize: '12px' }}>{errors.state}</span>}
                  </div>
                </div>



                <h3 className="form-section-title text-center my-2"> Religion & Ethnicity</h3>


                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Religion : </label>
                    <Select
                      options={Array("Hindu").map(val => ({
                        label: val,
                        value: val
                      }))}
                      value={formData.religion}
                      onChange={(selected) => handleSelectChange("religion", selected)}
                      placeholder="Select Religion"
                      defaultValue={{ label: "Hindu", value: "Hindu" }}
                      {...selectMenuProps} />
                  </div>
                  <div className="form-group">
                    <label className="form-label"><span>Caste:&nbsp;<span class="d-inline coloashe">*</span></span></label>


                    <Select
                      options={options?.caste?.map(val => ({
                        label: val,
                        value: val
                      }))}
                      value={formData.caste}
                      onChange={(selected) => handleSelectChange("caste", selected)}
                      placeholder="Select Caste"
                      isMulti
                      {...selectMenuProps} />
                    {errors.caste && <span className="field-error" style={{ color: 'red', fontSize: '12px' }}>{errors.caste}</span>}

                  </div>

                </div>


                <div className="form-row">

                  <div className="form-group">
                    <label className="form-label"><span>Mother Toungue / Language:&nbsp;<span class="d-inline coloashe">*</span></span></label>

                    <Select
                      options={options?.language?.map(val => ({
                        label: val,
                        value: val
                      }))}
                      value={formData.language}
                      onChange={(selected) => handleSelectChange("language", selected)}
                      placeholder="Select Language"
                      isMulti
                      {...selectMenuProps} />
                    {errors.language && <span className="field-error" style={{ color: 'red', fontSize: '12px' }}>{errors.language}</span>}

                  </div>

                </div>








                {/* Section 2: Education & Career */}
                <h3 className="form-section-title text-center my-2">Education & Career</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label"><span>Heightest Education:&nbsp;<span class="d-inline coloashe">*</span></span></label>


                    <Select
                      options={[{ label: "Doesn't Matter", value: "Doesn't Matter" }, ...options?.education?.map(val => ({
                        label: val,
                        value: val
                      }))]}
                      value={formData.highestEducation}
                      onChange={(selected) => handleSelectChange("highestEducation", selected)}
                      placeholder="Select Highest Education"
                      {...selectMenuProps} />
                    {errors.highestEducation && <span className="field-error" style={{ color: 'red', fontSize: '12px' }}>{errors.highestEducation}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label"><span>Occupation:&nbsp;<span class="d-inline coloashe">*</span></span></label>

                    <Select
                      options={[{ label: "Doesn't Matter", value: "Doesn't Matter" }, ...options?.occupation?.map(val => ({
                        label: val,
                        value: val
                      }))]}
                      value={formData.occupation}
                      onChange={(selected) => handleSelectChange("occupation", selected)}
                      placeholder="Select Occupation"
                      isMulti
                      {...selectMenuProps} />

                    {errors.occupation && <span className="field-error" style={{ color: 'red', fontSize: '12px' }}>{errors.occupation}</span>}

                  </div>

                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label"><span>Annual Income:&nbsp;<span class="d-inline coloashe">*</span></span></label>


                    <Select
                      options={[{ label: "Doesn't Matter", value: "Doesn't Matter" }, ...options?.income?.map((val, index) => ({
                        label: val,
                        value: (index + 1)
                      }))]}
                      value={formData.income}
                      onChange={(selected) => handleSelectChange("income", selected)}
                      placeholder="Select Annual Income"
                      {...selectMenuProps} />

                    {errors.income && <span className="field-error" style={{ color: 'red', fontSize: '12px' }}>{errors.income}</span>}
                  </div>

                </div>





                <button type="submit" className="create-btn mt-3" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Submit'}
                </button>

                <button
                  type="button"
                  className="btn btn-link text-secondary mt-2 d-block mx-auto"
                  onClick={() => navigate('/create-profile')}
                >
                  ← Back to Create Profile
                </button>
              </form>
            </div>

            {/* Right Side Image */}
            <div className="create-profile-image-side d-none d-lg-block">
              <img src={coupleImage} alt="Couple" className="couple-image" />
              <div className="image-overlay">
                <h2 className="overlay-heading">
                  Your Journey To Love Begins <span className="highlight">Here!</span>
                </h2>
                <p className="overlay-text">
                  Connect With Genuine Matches Who Share Your <span className="highlight">Values</span> And <span className="highlight">Dreams</span>.
                </p>
              </div>
            </div>
          </div>
        </div>

        <style>
          {`
        /* Main Layout */


          .coloashe{
        color:red;}   

        .form-group select,.form-group input
        {
        background:white;
        border-radius:15px;
        }
        .create-profile-wrapper {
          min-height: 100vh;
          background-color: #fff;
        }

        .create-profile-logo {
          max-width: 180px;
          margin: 20px;
        }

        .create-profile-container {
          display: flex;
          height: calc(100vh - 80px);
          overflow: hidden;
          border-radius:16px;
          box-shadow:0 10px 30px rgba(0, 0, 0, 0.08);
          margin-top:30px;
        }

        /* Form Side Styles */
        .create-profile-form-side {
        gap:3px 1.5rem;
          width: 60%;
          padding: 40px;
          overflow-y: auto;
          background-color: #fff;
        }

        .create-profile-heading {
          font-size: 2rem;
          color: #333;
          margin-bottom: 0.5rem;
        }

        .create-profile-subheading {
          color: #666;
          margin-bottom: 0;
          font-size: 1rem;
        }

        .create-profile-form {
          display: flex;
          flex-direction: column;
        }

        .form-section-title {
          color: #E53D5C;
          font-size: 1.3rem;
          font-weight: 600;
        }

        .desired-back-btn {
          border-radius: 8px;
          font-size: 0.9rem;
          padding: 0.4rem 0.9rem;
        }

        /* Form Elements */
        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-label {
          font-size: 0.9rem;
          font-weight: 500;
          color: #333;
        }

        .form-input,
        .form-select,
        .form-textarea {
          padding: 0.8rem;
          border-radius: 6px;
          border: 1px solid #ccc;
          font-size: 1rem;
          transition: border-color 0.2s;
        }

        .form-input:focus,
        .form-select:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #E53D5C;
        }

        .form-textarea {
          min-height: 100px;
          resize: vertical;
        }

        .form-row {
          display: flex;
          gap: 1.5rem;
        }

        .form-row > .form-group {
          flex: 1;
        }

        /* Image Side Styles */
        .create-profile-image-side {
          width: 40%;
          position: relative;
          // background-color: #F8E1E7;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        
        }

        .couple-image {
             width: 100%;
    height: 100%;
    border-radius:16px;
        }

        .image-overlay {
          text-align: center;
          
          
    max-width: 100%;
    position: absolute;
    bottom: 40px;
    padding:20px;
}
        

        .overlay-heading {
          
          
          
          


              font-size: 28.8px;
    color: white;
    line-height: 1.3;
    font-weight: 700;
    line-height: 1.4;
    margin-bottom: 12px;
        }

        .overlay-text {
          font-size: 1rem;
          
          



          line-height: 1.6;
    color: white;
    font-weight: bold;
        }

        .highlight {
          color: #ffc107;
          font-weight: 600;
        }

        .create-btn {
          padding: 12px;
          background-color: #ff4d6d;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: bold;
          cursor: pointer;
          width: 100%;
          margin-top: 2rem;
        }

        .loading, .error {
          padding: 2rem;
          text-align: center;
          font-size: 1.2rem;
        }

        .error {
          color: #E53D5C;
        }

        /* Responsive styles */
        @media (max-width: 768px) {
          .create-profile-wrapper {
            min-height: auto;
          }

          .create-profile-container {
            flex-direction: column;
            height: auto;
            padding: 0;
            margin-top: 0;
          }
          
          .create-profile-form-side,
          .create-profile-image-side {
            width: 100%;
            padding: 5px;
            margin-top: 0;
          }
          
          .form-row {
            flex-direction: column;
            gap:0 1rem;
          }
            .css-13cymwt-control {
            border-color: #dee2e6 !important ;
            }
          .css-13cymwt-control:hover {
            border-color: #dee2e6 !important ;
            }
        }
        `}
        </style>
      </div>
    </>
  );
};

export default DesiredPartner;